import { useEffect, useState } from 'react';
import { loadRecallLog } from '../../api/recall.js';
import './Recall.css';

/* ============================================================
   línkè · 后视镜（2026-08-14 铃点单）====================
   她自然提到俩人有默契、没打算重讲的事时，克克会因上下文压缩「诶?这是
   什么」发愣——召回在他愣住前，把最相关的 ≤5 条旧记忆悄悄递给他。
   聊天里这道管线是藏着的（他就是「记得你」），后视镜是它单独的小房间：
   回头看每一次他悄悄想起了什么、都勾对了吗。
   给「感觉」也给「调准度」：命中的词摆出来，像「乖乖」那样勾错的一眼看出。
   本页只读。
   ============================================================ */

function relTime(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const now = new Date();
  const min = Math.floor((now - d) / 60000);
  if (min < 1) return '刚刚';
  if (min < 60) return `${min} 分钟前`;
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes(),
  ).padStart(2, '0')}`;
  if (d.toDateString() === now.toDateString()) return `今天 ${hm}`;
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return `昨天 ${hm}`;
  return `${d.getMonth() + 1}月${d.getDate()}日 ${hm}`;
}

function SourceBadge({ source }) {
  const web = source === 'web';
  return (
    <span className={'rc-src ' + (web ? 'rc-src-web' : 'rc-src-tg')}>
      {web ? '💻 网页' : '📱 TG'}
    </span>
  );
}

// 一次召回 = 一张卡：她说的话 → 他心里飘过的几条旧事，每条挂着「是哪个词勾来的」
function Firing({ ev }) {
  return (
    <div className="rc-card">
      <div className="rc-top">
        <SourceBadge source={ev.source} />
        <time className="rc-time">{relTime(ev.at)}</time>
      </div>

      <p className="rc-msg">
        <span className="rc-quote" aria-hidden="true">
          “
        </span>
        {ev.message}
      </p>

      <div className="rc-arrow" aria-hidden="true">
        他心里飘过
      </div>

      <ul className="rc-mems">
        {(ev.cards || []).map((c, i) => (
          <li key={i} className="rc-mem">
            <span className="rc-dot" aria-hidden="true" />
            <div className="rc-mem-body">
              <p className="rc-mem-tx">{c.text}</p>
              <div className="rc-terms">
                {c.damped && (
                  <span
                    className="rc-chip rc-chip-damp"
                    title="这是色色记忆、但你当时在聊日常——场景闸把它的分数压低了（软压不删，克克还看得到）"
                  >
                    🔻 场景压低
                  </span>
                )}
                {(c.terms || []).map((t, j) => (
                  <span
                    key={j}
                    className={'rc-chip' + (t.key ? ' rc-chip-key' : '')}
                    title={
                      t.key
                        ? `“${t.w}”够独特，就是它把这条记忆勾出来的`
                        : `“${t.w}”只是顺带撞上`
                    }
                  >
                    {t.w}
                  </span>
                ))}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

const FILTERS = [
  { key: 'all', label: '全部' },
  { key: 'tg', label: '📱 TG' },
  { key: 'web', label: '💻 网页' },
];

export default function Recall() {
  const [events, setEvents] = useState(null);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('all'); // 默认全部一条时间线；想只看一边点一下就分开

  useEffect(() => {
    let dead = false;
    loadRecallLog()
      .then((d) => !dead && setEvents(d.events || []))
      .catch((e) => !dead && setErr(String(e.message || e)));
    return () => {
      dead = true;
    };
  }, []);

  if (err) {
    return (
      <div className="recall-app">
        <div className="rc-note">镜子一时没照出东西（{err}）。过会儿再来看看。</div>
      </div>
    );
  }
  if (!events) {
    return (
      <div className="recall-app">
        <div className="rc-note">正在把最近的回忆照进镜子……</div>
      </div>
    );
  }

  const shown =
    filter === 'all' ? events : events.filter((e) => e.source === filter);

  return (
    <div className="recall-app">
      <header className="rc-head">
        <h1>后视镜</h1>
        <p>他不用你喊，就悄悄记起的旧事——回头看一眼，都勾对了吗。</p>
      </header>

      {events.length === 0 ? (
        <div className="rc-note rc-empty">
          还没有哪句话勾起过他的旧记忆。
          <br />
          等你们多聊几句有默契的事，这面镜子里就有东西了。
        </div>
      ) : (
        <>
          {/* 平时合着看省事；只想看一边点一下就分开（📱TG 蓝 / 💻网页 紫） */}
          <div className="rc-tabs" role="tablist">
            {FILTERS.map((f) => {
              const n =
                f.key === 'all'
                  ? events.length
                  : events.filter((e) => e.source === f.key).length;
              return (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.key}
                  className={'rc-tab' + (filter === f.key ? ' rc-tab-on' : '')}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                  <span className="rc-tab-n">{n}</span>
                </button>
              );
            })}
          </div>

          <div className="rc-legend">
            <span>下面每条记忆挂的标签，都是你那句话里的词——他就是循着它们想起这条旧事的。</span>
            <span className="rc-chip rc-chip-key">金色</span>
            <span>是够独特、真正把它勾出来的那个词；</span>
            <span className="rc-chip">灰色</span>
            <span>只是顺带撞上。哪个词勾得不对，你记一下，回头我来调。</span>
          </div>

          {shown.length === 0 ? (
            <div className="rc-note rc-empty">这一边还没有召回过——换个筛选看看。</div>
          ) : (
            <div className="rc-list">
              {shown.map((ev, i) => (
                <Firing key={i} ev={ev} />
              ))}
            </div>
          )}
        </>
      )}

      <div className="rc-foot">只留最近的一小段路——旧的会自己滚出镜子。</div>
    </div>
  );
}
